import { Polygon, FeatureCollection, Feature } from '@turf/turf';
import { AuthorizableLAANCAdvisoryProperties } from './advisories/AuthorizableLAANCAdvisoryProperties';
import { IAdvisoryGeometry, IAdvisoryPropertiesV3 } from './advisories/IAdvisoryPropertiesV3';
import { IAggregateLAANCStatus } from './advisories/IAuthorizableLAANCAdvisoryProperties';
import { ICompositeLAANCAdvisoryProperties } from './advisories/ICompositeLAANCAdvisoryProperties';
import { ICoordinate } from './ICoordinate';
import { ISubdividableMissionProperties } from './ISubdividableMissionProperties';
import moment from 'moment';
import { IPersistableMissionFeatureCollection, PersistableMissionFeatureCollection } from './PersistableMissionFeatureCollection';
import { CompositeLAANCAdvisoryProvider } from './laanc/CompositeLAANCAdvisoryProvider';
import { TimeOfDayProvider } from '../TimeOfDayProvider';
import { ITimeOfDayAdvisoryProperties } from './advisories/ITimeOfDayAdvisoryProperties';

export type ICanFly = 'can-fly' | 'can-fly-with-laanc-authorization' | 'cannot-fly';

const laancStatusToCanFly: { [key in IAggregateLAANCStatus]: ICanFly } = {
    'authorized': 'can-fly',
    'authorizing': 'can-fly-with-laanc-authorization',
    'authorizing-failed': 'cannot-fly',
    'initialized': 'can-fly-with-laanc-authorization',
    'user-canceled': 'cannot-fly',
    'rescinded': 'cannot-fly',
    'rescind-acked': 'cannot-fly',
    'invalidated': 'cannot-fly',
    'invalidated-acked': 'cannot-fly',
    'user-closed': 'cannot-fly',
    'user-deleted': 'cannot-fly'
};

export class MissionFeatureCollectionFacade implements FeatureCollection<IAdvisoryGeometry, IAdvisoryPropertiesV3 | ISubdividableMissionProperties> {
    type: 'FeatureCollection' = 'FeatureCollection';
    readonly features: Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3 | ISubdividableMissionProperties>[];

    constructor(
        private mission: PersistableMissionFeatureCollection,
        private compositeLAANCAdvisoryProvider: CompositeLAANCAdvisoryProvider,
        private timeOfDayProvider: TimeOfDayProvider
    ) {
        // NOTE: this doesn't have composite in it... but that is likely OK since consumers should get composite
        // from this.getLAANCCompositeAdvisory
        this.features = mission.features;
    }

    canFly = (): ICanFly => {
        // note if we treat SubdividedAndMergedMission as immutable then we could cache this...
        // but not worth it yet as that is more likely to do harm and the optimization may not be worth much

        let canFly: ICanFly = 'cannot-fly';

        const anyBlock = this.getAdvisories().some(p => p.advisoryType === 'block');
        const laancComposite = this.getLAANCCompositeAdvisory();

        if (!anyBlock && laancComposite) {
            canFly = 'can-fly-with-laanc-authorization';
        } else if (!anyBlock) {
            canFly = 'can-fly';
        }

        if (canFly === 'can-fly-with-laanc-authorization') {
            const laancStatus = this.getAPLAANCStatus();
            canFly = laancStatusToCanFly[laancStatus];
        }

        return canFly;
    }

    getMissionRadius = (): Feature<Polygon, ISubdividableMissionProperties> => {
        return this.features.find(f => f.properties.source === 'mission-radius') as Feature<Polygon, ISubdividableMissionProperties>;
    }

    getMissionCenter = (): ICoordinate => {
        return this.getMissionRadius().properties.center;
    }

    getMissionRadiusMeters = (): number => {
        return this.getMissionRadius().properties.radiusMeters;
    }

    getPersistable = (): IPersistableMissionFeatureCollection => {
        return this.mission;
    }

    getAPLAANCStatus = (): IAggregateLAANCStatus => {
        let statuses = this.getLAANCAuthorizeableAdvisoryFeatures().map(f => f.properties.getAPLAANCStatus({ endTime: this.getMissionRadius().properties.endTime }));

        statuses = statuses.map(s => {
            // any that are never-authorized
            // are effectively initialized for the purpose of aggregation
            return s === 'never-authorized' ? 'initialized' : s;
        });

        const inPast = Date.now() > moment(this.getMissionRadius().properties.endTime).valueOf();

        if (statuses.length === 0) {
            throw new Error('a laanc mission with no authorization advisories, this should not happen');
        }

        const anyFailed = statuses.some(s => s === 'authorizing-failed');
        if (anyFailed) {
            return 'authorizing-failed';
        }

        const anyRescinded = statuses.some(s => s === 'rescinded');
        if (anyRescinded) {
            return 'rescinded';
        }

        const allInTerminalState = statuses.every(s => ['rescind-acked', 'user-canceled', 'user-deleted', 'invalidated-acked'].includes(s));

        const anyRescindAcked = statuses.some(s => s === 'rescind-acked');
        if (anyRescindAcked && allInTerminalState) {
            return 'rescind-acked';
        }
        if (anyRescindAcked && !allInTerminalState) {
            return 'rescinded';
        }

        const allInitialized = statuses.every(s => s === 'initialized');
        if (allInitialized) {
            return 'initialized';
        }

        const anyInvalidated = statuses.some(s => s === 'invalidated');
        if(anyInvalidated){
            return 'invalidated';
        }

        const anyInvalidateAcked = statuses.some(s => s === 'invalidated-acked');
        if (anyInvalidateAcked && allInTerminalState){
            return 'invalidated-acked';
        }
        if (anyInvalidateAcked && !allInTerminalState){
            return 'invalidated';
        }

        // the next 3 are intentionally later in the fn so other
        // permutations already happened
        const anyUserDeleted = statuses.some(s => s === 'user-deleted');
        if (anyUserDeleted) {
            return 'user-deleted';
        }

        const anyUserCanceled = statuses.some(s => s === 'user-canceled');
        if (anyUserCanceled) {
            return 'user-canceled';
        }

        const anyUserClosed = statuses.some(s => s === 'user-closed');
        if (anyUserClosed) {
            return 'user-closed';
        }

        const allAuthorized = statuses.every(s => s === 'authorized');
        const anyAuthorized = statuses.some(s => s === 'authorized');
        if (allAuthorized || (inPast && anyAuthorized)) { // and not rescinded, canceled, etc (handled above)
            // this is not an ideal state, where we either need to return partially-authorized
            // at this time, especially since this is only used to stop polling
            // this seems like the best answer (to include that you can look into the actual
            // advisories to see it is partially authorized)
            return 'authorized';
        }

        const anyAuthorizing = statuses.some(s => ['authorizing', 'initialized'].includes(s));
        if (anyAuthorizing && inPast) {
            return 'initialized';
        }
        if (anyAuthorizing) {
            return 'authorizing';
        }

        throw new Error(`unhandled ${JSON.stringify({ statuses, inPast })}`);
    }

    getAdvisoryFeatures = (): Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3>[] => {
        return this.features.filter(f => f.properties.source !== 'mission-radius') as Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3>[];
    }

    getAdvisories = (): IAdvisoryPropertiesV3[] => {
        const advisories = this.getAdvisoryFeatures().map(f => f.properties);

        // TODO: remove this hack after deploying tiles with sata unblocked
        advisories.forEach(a => {
            if (a.source === 'airspace-boundary' && (a as any).GLOBAL_ID !== 'boundary-4719D105-C935-4EB7-B31D-6D42BB1450FE') {
                a.advisoryType = 'advise';
            }
        });

        const laancComposite = this.getLAANCCompositeAdvisory();
        if (laancComposite){
            advisories.push(laancComposite);
        }
        return advisories.concat([this.createTimeOfDayAdvisory()]);
    }

    private createTimeOfDayAdvisory = (): ITimeOfDayAdvisoryProperties => {
        const { center, startTime, endTime } = this.getMissionRadius().properties;
        const relevantTimesOfDay = this.timeOfDayProvider.getTimesOfDay({ center, startTime, endTime });

        return {
            advisoryType: 'advise',
            source: 'time-of-day',
            relevantTimesOfDay
        };
    }

    getLAANCCompositeAdvisory = (): ICompositeLAANCAdvisoryProperties | undefined => {
        return this.compositeLAANCAdvisoryProvider.getCompositeAdvisory(this);
    }

    getLAANCAuthorizeableAdvisoryFeatures = (): Feature<Polygon, AuthorizableLAANCAdvisoryProperties>[] => {
        return this.getAdvisoryFeatures()
            .filter(a => a.properties.source === 'uasfm+authorizable') as Feature<Polygon, AuthorizableLAANCAdvisoryProperties>[];
    }

    getMaxAltitude = (): number => {
        if (this.canFly() === 'cannot-fly') {
            return 0;
        } else if (this.canFly() === 'can-fly-with-laanc-authorization') {
            return this.getLAANCCompositeAdvisory()!.CEILING;
        } else {
            return 400;
        }
    }
}
